import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { UserRole } from "../models/userRole.model";
import { GridService } from "../shared/grid/grid.service";
import { catchError, Observable, of } from "rxjs";
import { shareReplay, tap } from "rxjs/operators";
import { ToastService } from "./toast.service";
import { ObjectIdLookup } from "../models/objectIdLookup.model";
import { Lookup } from "../models/lookup.model";

@Injectable({
    providedIn: "root",
})
export class UserRoleService extends GridService<UserRole> {
    private cache$: Observable<Lookup[]> | null = null;

    constructor(
        protected http: HttpClient,
        private toastService: ToastService,
    ) {
        super("UserRole", http);
    }

    getAll(filterBy: string = ""): Observable<Lookup[]> {
        if (filterBy) {
            const params = new HttpParams().set("filterBy", filterBy);
            return this.http.get<Lookup[]>(
                this.getApiUrl(`${this.controllerName}/GetAll`),
                { params },
            );
        }
        if (!this.cache$) {
            this.cache$ = this.http
                .get<Lookup[]>(
                    this.getApiUrl(`${this.controllerName}/GetAll`),
                )
                .pipe(shareReplay({ bufferSize: 1, refCount: true }));
        }
        return this.cache$;
    }

    clearCache(): void {
        this.cache$ = null;
    }

    override create(payload: UserRole) {
        return super.create(payload).pipe(tap(() => this.clearCache()));
    }

    override update(payload: UserRole) {
        return super.update(payload).pipe(tap(() => this.clearCache()));
    }

    override delete(id: string) {
        return super.delete(id).pipe(tap(() => this.clearCache()));
    }

    override patch(id: string, payload: Partial<UserRole>) {
        return super.patch(id, payload).pipe(tap(() => this.clearCache()));
    }

    override createRange(payload: UserRole[]) {
        return super.createRange(payload).pipe(tap(() => this.clearCache()));
    }

    getUserRoles() {
        return this.http
            .get<
                UserRole[]
            >(this.getApiUrl(`${this.controllerName}/GetUserRoles`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading user roles",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getObjectIds(): Observable<ObjectIdLookup[]> {
        return this.http
            .get<
                ObjectIdLookup[]
            >(this.getApiUrl(`${this.controllerName}/GetObjectIds`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading object IDs",
                        true,
                    );
                    return of([]);
                }),
            );
    }
}
